import { BusModuleRoute } from 'frontend/bus-module-routes'
import { BusTimetable } from 'frontend/bus-timetables'
import { ClientGroup } from 'frontend/client-groups'
import { Cluster } from 'frontend/clusters'
import HumanizedDuration from 'frontend/common/humanized-duration.vue'
import Text from 'frontend/common/text.vue'
import { DriverCategory } from 'frontend/driver-categories'
import { DriverSource, ManualDriverSource } from 'frontend/driver-sources'
import { Driver } from 'frontend/drivers'
import { Location, LocationWithTp } from 'frontend/locations'
import { Osl } from 'frontend/osls'
import { Shift } from 'frontend/shifts'
import { TransportationPoint } from 'frontend/transportation-points'
import { VehicleCategory } from 'frontend/vehicle-categories'
import { VehicleModel } from 'frontend/vehicle-models'
import { VehicleSource } from 'frontend/vehicle-sources'
import { VehicleType } from 'frontend/vehicle-types'
import { Vehicle } from 'frontend/vehicles'
import { camelCase, padStart, uniqBy } from 'lodash'
import { markRaw } from 'vue'

import { BusIdDisplay, SOURCES } from './index'
import AasmState from './shuttle-transfer-aasm-state.vue'

export const FIELDS = [
  'id',
  'eid',
  'start_at',
  'end_at',
  'updated_at',
  'driver_comment',
  'bus_id',
  'aasm_state',
  'source',
  'remote_id',
  'calculated_bus_outbound_buffer',
  'calculated_bus_inbound_buffer',
  'use_manual_driver_source_values',
  'manual_default_shift_length',
  'manual_min_time_between_shifts',
  'manual_min_break_time',
  'manual_max_break_time',
  'manual_max_cont_driving_time',
  'min_pax',
  'is_empty_run',
  'sum_of_passengers_count',
  { vehicle_type: ['id', 'name'] },
  { vehicle_source: ['id', 'name', 'billable', 'maintainable'] },
  { driver_source: ['id', 'name'] },
  { bus_timetable: ['id', 'name'] },
  { bus_route: ['id', 'name', 'color'] },
  { original_bus_route: ['id', 'name', 'color'] },
  { original_bus_timetable: ['id', 'name'] },
  { client_groups: ['id', 'name', 'background_color', 'border_color', 'font_color'] },
  {
    operational_service_level: [
      'id',
      'name',
      'font_color',
      'background_color',
      'border_color',
      'description',
    ],
  },
  {
    shuttle_points: [
      'id',
      'position',
      'arrival_at',
      'passengers_count',
      {
        location: ['id', 'name', 'time_zone', { cluster: ['id', 'name', 'time_zone'] }],
      },
    ],
  },
  {
    day_labels: ['id', 'name', 'font_color', 'background_color', 'border_color'],
  },
  { start_location: ['id', 'name', 'time_zone', { cluster: ['id', 'name', 'time_zone'] }] },
  { end_location: ['id', 'name', 'time_zone', { cluster: ['id', 'name', 'time_zone'] }] },
  { start_transportation_point: ['id', 'name'] },
  { end_transportation_point: ['id', 'name'] },
  {
    shift: [
      'id',
      'kind',
      'eid',
      {
        staging_location: ['id', 'name', 'time_zone', { cluster: ['id', 'name', 'time_zone'] }],
      },
      { vapp_types: ['id', 'name', 'archived_at'] },
      {
        driver: [
          'id',
          'name',
          'surname',
          'assigned_phone',
          'personal_phone',
          'primary_phone',
          { cluster: ['id', 'name', 'time_zone'] },
          {
            driver_category: ['id', 'name', 'background_color', 'border_color', 'font_color'],
          },
          {
            driver_source: ['id', 'name'],
          },
        ],
      },
      {
        vehicle: [
          'id',
          'fleet_number',
          'registration_plate',
          { vapp_types: ['id', 'name', 'archived_at'] },
          { cluster: ['id', 'name', 'time_zone'] },
          { vehicle_model: ['id', 'name', { vehicle_brand: ['id', 'name'] }] },
          { vehicle_type: ['id', 'name'] },
          { vehicle_source: ['id', 'name', 'billable', 'maintainable'] },
          {
            vehicle_category: ['id', 'name', 'background_color', 'border_color', 'font_color'],
          },
        ],
      },
    ],
  },
]

export const SUBSCRIPTIONS = {
  items: 'ShuttleTransfersChannel',
  operationalServiceLevels: 'OperationalServiceLevelsSimpleChannel',
  locations: 'LocationsSimpleChannel',
  shifts: 'ShiftsSimpleChannel',
  drivers: 'DriversDetailedChannel',
  driverCategories: 'DriverCategoriesSimpleChannel',
  vehicles: 'VehiclesDetailedChannel',
  vehicleTypes: 'VehicleTypesSimpleChannel',
  clientGroups: 'ClientGroupsSimpleChannel',
  vehicleCategories: 'VehicleCategoriesSimpleChannel',
  vehicleModels: 'VehicleModelsSimpleChannel',
  vehicleBrands: 'VehicleBrandsSimpleChannel',
  transportationPoints: 'TransportationPointsSimpleChannel',
  vehicleSources: 'VehicleSourcesSimpleChannel',
  driverSources: 'DriverSourcesSimpleChannel',
  busRoutes: 'BusRoutesFlatChannel',
  busTimetables: 'BusTimetablesFlatChannel',
}

export const DEFAULT_COLUMNS = [
  'eid',
  'startAt',
  'aasmState',
  'operationalServiceLevel',
  'vehicleType',
  'clientGroups',
  'pu',
  'do',
  'otherLocations',
  'dayLabels',
  'shift',
  'assignedDriver',
  'assignedVehicle',
  'source',
  'updatedAt',
]

const otherLocations = st => {
  return uniqBy(
    (st.shuttlePoints || [])
      .filter(sp => ![st.startLocation?.id, st.endLocation?.id].includes(sp.location?.id))
      .sort((a, b) => a.position - b.position),
    el => el.location?.id,
  ).map(el => el.location)
}

export const COLUMNS_CONFIG = {
  eid: {
    label: 'EID',
    header: {
      sort: 'eid',
    },
    cell: {
      kind: 'simple',
      path: 'eid',
    },
  },

  calculatedBusOutboundBuffer: {
    label: 'Outbound buffer (min)',
    header: {
      sort: 'calculated_bus_outbound_buffer',
    },
    cell: {
      kind: 'component',
      component: markRaw(HumanizedDuration),
      componentProps: st => ({
        minutes: st.calculatedBusOutboundBuffer > 0 ? st.calculatedBusOutboundBuffer : null,
      }),
    },
  },

  startAt: {
    label: 'Start at',
    header: {
      sort: 'startAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({ t: item.startAt, tz: item.startLocation?.timeZone }),
    },
  },

  startAtTime: {
    label: 'Start Time',
    header: {
      sort: 'startAtTime',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.startAt,
        tz: item.startLocation?.timeZone,
        format: 'timeOnly',
      }),
    },
  },

  startAtDate: {
    label: 'Start Date',
    header: {
      sort: 'startAtDate',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.startAt,
        tz: item.startLocation?.timeZone,
        format: 'dateOnly',
      }),
    },
  },

  endAt: {
    label: 'End at',
    header: {
      sort: 'endAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({ t: item.endAt, tz: item.endLocation?.timeZone }),
    },
  },

  endAtTime: {
    label: 'End Time',
    header: {
      sort: 'endAtTime',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.endAt,
        tz: item.endLocation?.timeZone,
        format: 'timeOnly',
      }),
    },
  },

  endAtDate: {
    label: 'End Date',
    header: {
      sort: 'endAtDate',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.endAt,
        tz: item.endLocation?.timeZone,
        format: 'dateOnly',
      }),
    },
  },

  calculatedBusInboundBuffer: {
    label: 'Inbound buffer (min)',
    header: {
      sort: 'calculated_bus_inbound_buffer',
    },
    cell: {
      kind: 'component',
      component: markRaw(HumanizedDuration),
      componentProps: st => ({
        minutes: st.calculatedBusInboundBuffer > 0 ? st.calculatedBusInboundBuffer : null,
      }),
    },
  },

  aasmState: {
    label: 'State',
    header: {
      sort: 'aasmState',
    },
    cell: {
      kind: 'component',
      component: markRaw(AasmState),
      componentProps: item => ({
        aasmState: item.aasmState,
      }),
    },
  },

  vehicleType: {
    label: 'Required vehicle type',
    header: {},
    cell: {
      kind: 'component',
      component: markRaw(VehicleType),
      componentProps: el => ({
        vehicleType: el.vehicleType,
      }),
    },
  },

  operationalServiceLevel: {
    label: 'OSL',
    header: {
      sort: 'operationalServiceLevelName',
    },
    cell: {
      kind: 'component',
      component: markRaw(Osl),
      componentProps: el => ({
        osl: el.operationalServiceLevel,
      }),
    },
  },

  clientGroups: {
    label: 'Client groups',
    header: {
      // sort: 'clientGroupsName',
    },
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: el => ({
        items: el.clientGroups,
        limit: 5,
        component: markRaw(ClientGroup),
        componentProps: ell => ({ clientGroup: ell }),
      }),
    },
  },

  busRoute: {
    label: 'Bus route',
    header: {
      sort: 'busRouteName',
    },
    cell: {
      kind: 'component',
      component: markRaw(BusModuleRoute),
      componentProps: tb => ({
        busModuleRoute: tb.busRoute,
      }),
    },
  },

  originalBusRoute: {
    label: 'Original bus route',
    header: {
      sort: 'originalBusRouteName',
    },
    cell: {
      kind: 'component',
      component: markRaw(BusModuleRoute),
      componentProps: tb => ({
        busModuleRoute: tb.originalBusRoute,
      }),
    },
  },

  originalBusTimetable: {
    label: 'Original bus timetable',
    header: {
      sort: 'originalBusTimetableName',
    },
    cell: {
      kind: 'component',
      component: markRaw(BusTimetable),
      componentProps: sj => ({
        busTimetable: sj.originalBusTimetable,
      }),
    },
  },

  startCluster: {
    label: 'First Cluster',
    header: {
      sort: 'startLocationClusterName',
    },
    cell: {
      kind: 'component',
      component: markRaw(Cluster),
      componentProps: tb => ({
        cluster: tb.startLocation?.cluster,
      }),
    },
  },

  startLocation: {
    label: 'First Location',
    header: {
      sort: 'startLocationName',
    },
    cell: {
      kind: 'component',
      component: markRaw(Location),
      componentProps: tb => ({
        location: tb.startLocation,
        // nowrap: true,
        minWidth: 250,
      }),
    },
  },

  startTransportationPoint: {
    label: 'First TP',
    tooltip: 'First transportation point',
    header: {
      sort: 'startTransportationPointName',
    },
    cell: {
      kind: 'component',
      component: markRaw(TransportationPoint),
      componentProps: tb => ({
        transportationPoint: tb.startTransportationPoint,
        kindIn: "['shuttle']",
        nowrap: true,
      }),
    },
  },

  pu: {
    label: 'First location with TP',
    tooltip: 'First location with transportation point',
    header: {
      sort: 'startLocationName',
    },
    cell: {
      kind: 'component',
      component: markRaw(LocationWithTp),
      componentProps: el => ({
        item: el,
        kind: 'start',
        // nowrap: true,
        minWidth: 250,
      }),
    },
  },

  endCluster: {
    label: 'Last Cluster',
    header: {
      sort: 'endLocationClusterName',
    },
    cell: {
      kind: 'component',
      component: markRaw(Cluster),
      componentProps: tb => ({
        cluster: tb.endLocation?.cluster,
      }),
    },
  },

  endLocation: {
    label: 'Last Location',
    header: {
      sort: 'endLocationName',
    },
    cell: {
      kind: 'component',
      component: markRaw(Location),
      componentProps: tb => ({
        location: tb.endLocation,
        // nowrap: true,
        minWidth: 250,
      }),
    },
  },

  endTransportationPoint: {
    label: 'Last TP',
    tooltip: 'Last transportation point',

    header: {
      sort: 'endTransportationPointName',
    },
    cell: {
      kind: 'component',
      component: markRaw(TransportationPoint),
      componentProps: tb => ({
        transportationPoint: tb.endTransportationPoint,
        kindIn: "['shuttle']",
        nowrap: true,
      }),
    },
  },

  do: {
    label: 'Last location with TP',
    tooltip: 'Last location with transportation point',
    header: {
      sort: 'endLocationName',
    },
    cell: {
      kind: 'component',
      component: markRaw(LocationWithTp),
      componentProps: tb => ({
        item: tb,
        kind: 'end',
        // nowrap: true,
        minWidth: 250,
      }),
    },
  },

  busId: {
    label: 'BUS ID',
    header: {
      sort: 'busIdOrShuttleServiceBusId',
    },
    cell: {
      kind: 'component',
      component: BusIdDisplay,
      componentProps: el => ({
        item: el,
      }),
    },
  },

  source: {
    label: 'Source',
    header: {
      sort: 'sourceWithRemoteId',
    },
    cell: {
      kind: 'function',
      fn: el => {
        const name = SOURCES.find(s => s.value == (el.source || 'manually_created'))?.label
        if (
          ['imported', 'synchronized_via_api', 'overwritten_api_synchronization'].includes(
            el.source,
          )
        ) {
          return `${name} with Remote ID ${el.remoteId}`
        } else {
          return name
        }
      },
    },
  },
  sumOfPassengersCount: {
    label: 'ST Pax',
    header: {
      sort: 'sum_of_passengers_count',
    },
    cell: {
      kind: 'simple',
      path: 'sumOfPassengersCount',
    },
  },
  passengersCountPerPoint: {
    label: 'ST Pax per point',
    header: {
      sort: 'sum_of_passengers_count',
    },
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: st => ({
        items: st.shuttlePoints
          .sort((a, b) => a.position - b.position)
          .map(
            (el, i) =>
              `${padStart(el.passengersCount.toString(), 2, '0')} at #${i + 1} ${
                el.location?.name
              }`,
          )
          .filter(x => !!x),
        limit: 5,
        component: markRaw(Text),
        componentProps: item => ({ text: item }),
      }),
    },
  },
  remoteId: {
    label: 'Remote ID',
    header: {
      sort: 'remoteId',
    },
    cell: {
      class: 'text-nowrap',
      kind: 'simple',
      path: 'remoteId',
    },
  },
  otherLocations: {
    label: 'Other locations in route',
    header: {
      // sort: 'clientFullName',
    },
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: el => ({
        items: otherLocations(el),
        limit: 5,
        component: markRaw(Location),
        componentProps: l => ({ location: l, noCluster: true }),
      }),
    },
  },
  dayLabels: {
    label: 'Occasion',
    header: {},
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: tb => ({
        items: tb.dayLabels,
        limit: 5,
        component: 'erebus-label',
        componentProps: c => ({ item: c }),
      }),
    },
  },

  shift: {
    label: 'Assigned Shift',
    header: {
      sort: 'shiftStagingLocationName',
    },
    cell: {
      kind: 'component',
      component: markRaw(Shift),
      componentProps: tb => ({
        shift: tb.shift,
        detailed: true,
      }),
    },
  },

  driverSource: {
    label: 'Required driver source',
    header: {
      sort: 'driverSourceName',
    },
    cell: {
      kind: 'component',
      component: markRaw(ManualDriverSource),
      componentProps: sj => ({
        driverSource: sj.driverSource,
        useManualDriverSourceValues: sj.useManualDriverSourceValues,
        manualDefaultShiftLength: sj.manualDefaultShiftLength,
        manualMinTimeBetweenShifts: sj.manualMinTimeBetweenShifts,
        manualMinBreakTime: sj.manualMinBreakTime,
        manualMaxBreakTime: sj.manualMaxBreakTime,
        manualMaxContDrivingTime: sj.manualMaxContDrivingTime,
      }),
    },
  },

  assignedDriver: {
    label: 'Driver',
    header: {
      sort: 'shiftDriverReverseFullName',
    },
    cell: {
      kind: 'component',
      component: markRaw(Driver),
      componentProps: tb => ({
        driver: tb.shift?.driver,
        detailed: true,
      }),
    },
  },

  assignedDriverName: {
    label: 'Driver name',
    header: {
      sort: 'shiftDriverName',
    },
    cell: {
      kind: 'simple',
      path: 'shift.driver.name',
    },
  },

  assignedDriverSurname: {
    label: 'Driver surname',
    header: {
      sort: 'shiftDriverSurname',
    },
    cell: {
      kind: 'simple',
      path: 'shift.driver.surname',
    },
  },

  assignedDriverPhoneNumber: {
    label: 'Driver phone number',
    header: {
      sort: 'shiftDriverMainPhone',
    },
    cell: {
      kind: 'component',
      component: 'phone-number',
      componentProps: tb => ({
        phoneNumber: tb.shift?.driver?.[camelCase(tb.shift?.driver?.primaryPhone || '')],
      }),
    },
  },

  assignedDriverCategory: {
    label: 'Driver category',
    header: {
      sort: 'shiftDriverDriverCategoryName',
    },
    cell: {
      kind: 'component',
      component: markRaw(DriverCategory),
      componentProps: tb => ({
        driverCategory: tb.shift?.driver?.driverCategory,
      }),
    },
  },

  assignedDriverSource: {
    label: 'Driver source',
    header: {
      sort: 'shiftDriverDriverSourceName',
    },
    cell: {
      kind: 'component',
      component: markRaw(DriverSource),
      componentProps: st => ({
        driverSource: st.shift?.driver?.driverSource,
      }),
    },
  },

  driverComment: {
    label: 'Driver comment',
    header: {
      sort: 'driver_comment',
    },
    cell: {
      kind: 'component',
      component: 'ellipsis-with-tooltip',
      componentProps: item => ({
        text: item.driverComment,
        limit: 250,
      }),
    },
  },

  minPax: {
    label: 'Min vehicle capacity',
    header: {
      sort: 'min_pax',
    },
    cell: {
      kind: 'simple',
      path: 'minPax',
    },
  },

  vehicleSource: {
    label: 'Required vehicle source',
    header: {
      sort: 'vehicleSourceName',
    },
    cell: {
      kind: 'component',
      component: markRaw(VehicleSource),
      componentProps: sj => ({
        vehicleSource: sj.vehicleSource,
      }),
    },
  },

  busTimetable: {
    label: 'Bus timetable',
    header: {
      sort: 'busTimetableName',
    },
    cell: {
      kind: 'component',
      component: markRaw(BusTimetable),
      componentProps: sj => ({
        busTimetable: sj.busTimetable,
      }),
    },
  },

  assignedVehicle: {
    label: 'Vehicle',
    header: {
      sort: ['shiftVehicleFleetNumberPrefix', 'shiftVehicleFleetNumberInt'],
    },
    cell: {
      kind: 'component',
      component: markRaw(Vehicle),
      componentProps: tb => ({
        vehicle: tb.shift?.vehicle,
        detailed: true,
      }),
    },
  },

  assignedVehicleFleetNumber: {
    label: 'Vehicle fleet number',
    header: {
      sort: ['shiftVehicleFleetNumberPrefix', 'shiftVehicleFleetNumberInt'],
    },
    cell: {
      kind: 'simple',
      path: 'shift.vehicle.fleetNumber',
    },
  },

  assignedVehicleRegistrationPlate: {
    label: 'Vehicle reg. plate',
    tooltip: 'Vehicle registration plate',
    header: {
      sort: 'shiftVehicleRegistrationPlate',
    },
    cell: {
      kind: 'simple',
      path: 'shift.vehicle.registrationPlate',
    },
  },

  assignedVehicleCluster: {
    label: 'Vehicle cluster',
    header: {
      sort: 'shiftVehicleVehicleClusterName',
    },
    cell: {
      kind: 'component',
      component: markRaw(Cluster),
      componentProps: tb => ({
        cluster: tb.shift?.vehicle?.cluster,
      }),
    },
  },

  assignedVehicleCategory: {
    label: 'Vehicle category',
    header: {
      sort: 'shiftVehicleVehicleCategoryName',
    },
    cell: {
      kind: 'component',
      component: markRaw(VehicleCategory),
      componentProps: tb => ({
        vehicleCategory: tb.shift?.vehicle?.vehicleCategory,
      }),
    },
  },

  assignedVehicleType: {
    label: 'Assigned vehicle type',
    header: {
      sort: 'shiftVehicleVehicleTypeName',
    },
    cell: {
      kind: 'component',
      component: markRaw(VehicleType),
      componentProps: tb => ({
        vehicleType: tb.shift?.vehicle?.vehicleType,
      }),
    },
  },

  assignedVehicleModel: {
    label: 'Vehicle model',
    header: {
      sort: 'shiftVehicleVehicleModelName',
    },
    cell: {
      kind: 'component',
      component: markRaw(VehicleModel),
      componentProps: tb => ({
        vehicleModel: tb.shift?.vehicle?.vehicleModel,
      }),
    },
  },

  shiftDetails: {
    label: 'Assigned Shift Details',
    header: {
      sort: 'shiftStagingLocationName',
    },
    cell: {
      kind: 'component',
      component: markRaw(Shift),
      componentProps: tb => ({
        shift: tb.shift,
        detailed: true,
        withDriver: true,
        withVehicle: true,
      }),
    },
  },

  updatedAt: {
    label: 'Updated datetime',
    header: {
      sort: 'updatedAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({ t: item.updatedAt }),
    },
  },

  updatedAtTime: {
    label: 'Updated Time',
    header: {
      sort: 'updatedAtTime',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.updatedAt,
        format: 'timeOnly',
      }),
    },
  },

  updatedAtDate: {
    label: 'Updated Date',
    header: {
      sort: 'updatedAtDate',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.updatedAt,
        format: 'dateOnly',
      }),
    },
  },

  isEmptyRun: {
    label: 'Is empty run',
    header: {},
    cell: {
      kind: 'component',
      component: 'true-false-label',
      componentProps: item => ({ item: item.isEmptyRun, revertColors: true }),
    },
  },
}
